import { useState, useEffect } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UploadSimple, Trash, MapPin, Stack } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPE_COLOR = {
  "rock-chip": "bg-priority-high text-white", soil: "bg-priority-med text-black",
  "drill-collar": "bg-[#8b5cf6] text-white", sample: "bg-secondary",
};

export default function GeoPanel({ project, reload, setOverlay, activeOverlayKey }) {
  const id = project.id;
  const data = project.field_data || [];
  const aux = project.aux_layers || [];
  const bbox = project.boundary_metrics?.bbox;
  const [gName, setGName] = useState("");
  const [gType, setGType] = useState("geochem");
  const [gBusy, setGBusy] = useState(false);
  const [spectralW, setSpectralW] = useState(60);
  const [auxW, setAuxW] = useState({});
  const [presets, setPresets] = useState([]);
  useEffect(() => { api.get("/me/blend-presets").then((r) => setPresets(r.data)).catch(() => {}); }, []);

  const savePreset = async () => {
    const name = window.prompt("Save blend recipe as (e.g. 60% spectral / 40% soil-Au)");
    if (!name) return;
    const auxSel = aux.map((l) => ({ name: l.name, weight: Number(auxW[l.name] ?? 20) }));
    try { const { data: r } = await api.post("/me/blend-presets", { name, spectral_weight: Number(spectralW), aux: auxSel }); setPresets(r); toast.success("Recipe saved"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const applyPreset = async (p) => {
    setSpectralW(p.spectral_weight);
    const w = {}; (p.aux || []).forEach((a) => { w[a.name] = a.weight; }); setAuxW(w);
    try { await api.post(`/projects/${id}/prospectivity/blend`, { spectral_weight: p.spectral_weight, aux: p.aux }); toast.success(`Applied "${p.name}"`); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delPreset = async (name) => {
    try { const { data: r } = await api.delete(`/me/blend-presets/${encodeURIComponent(name)}`); setPresets(r); }
    catch (e) { toast.error(apiError(e)); }
  };

  const uploadAux = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!gName.trim()) { toast.error("Enter a layer name first"); e.target.value = ""; return; }
    const fd = new FormData();
    fd.append("file", file); fd.append("name", gName.trim()); fd.append("dtype", gType);
    setGBusy(true);
    try {
      const { data: r } = await api.post(`/projects/${id}/aux-grid/upload`, fd);
      toast.success(`Grid "${r.layer.name}" added`); setGName(""); reload();
    } catch (err) { toast.error(apiError(err)); } finally { setGBusy(false); e.target.value = ""; }
  };
  const clearAux = async () => {
    try { await api.delete(`/projects/${id}/aux-grid`); toast.success("Grids cleared"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const blend = async () => {
    const auxSel = aux.map((l) => ({ name: l.name, weight: Number(auxW[l.name] ?? 20) }));
    try {
      await api.post(`/projects/${id}/prospectivity/blend`, { spectral_weight: Number(spectralW), aux: auxSel });
      toast.success("Prospectivity blended - targets updated"); reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: r } = await api.post(`/projects/${id}/field-data/upload`, fd);
      toast.success(`${r.added} field samples imported`);
      reload();
    } catch (err) { toast.error(apiError(err)); }
    e.target.value = "";
  };
  const clear = async () => {
    try { await api.delete(`/projects/${id}/field-data`); toast.success("Field data cleared"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const byType = data.reduce((a, s) => { a[s.type] = (a[s.type] || 0) + 1; return a; }, {});
  const vals = data.map((s) => s.value).filter((v) => v != null);
  const vMin = vals.length ? Math.min(...vals) : 0, vMax = vals.length ? Math.max(...vals) : 1;
  const grade = (v) => {
    if (v == null) return "#94a3b8";
    const t = vMax > vMin ? (v - vMin) / (vMax - vMin) : 0.5;
    const stops = [[42, 157, 143], [233, 196, 106], [230, 57, 70]];
    const i = t < 0.5 ? 0 : 1, f = t < 0.5 ? t * 2 : (t - 0.5) * 2;
    const c = stops[i].map((a, k) => Math.round(a + (stops[i + 1][k] - a) * f));
    return `rgb(${c[0]},${c[1]},${c[2]})`;
  };

  return (
    <div className="space-y-4">
      <label className="border-2 border-dashed border-border rounded-md p-6 flex flex-col items-center gap-2 cursor-pointer hover:border-primary transition-colors" data-testid="geo-upload">
        <UploadSimple size={26} className="text-muted-foreground" />
        <span className="text-sm font-medium">Upload geological & field data</span>
        <span className="text-xs text-muted-foreground text-center">Rock-chip · Soil · Stream sediment · Drill collars · Assays<br />CSV · XLSX · GeoJSON · KML</span>
        <input type="file" className="hidden" accept=".csv,.xlsx,.geojson,.json,.kml,.txt" onChange={upload} data-testid="geo-file-input" />
      </label>

      {data.length > 0 && (
        <>
          <div className="flex items-center justify-between">
            <div className="flex gap-2 flex-wrap">
              {Object.entries(byType).map(([t, c]) => <Badge key={t} className={TYPE_COLOR[t] || "bg-secondary"}>{t}: {c}</Badge>)}
            </div>
            <button className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1" onClick={clear} data-testid="geo-clear"><Trash size={12} />Clear</button>
          </div>
          {vals.length > 0 && (
            <div data-testid="assay-legend">
              <p className="overline mb-1">Assay Grading</p>
              <div className="h-3 rounded" style={{ background: "linear-gradient(to right, rgb(42,157,143), rgb(233,196,106), rgb(230,57,70))" }} />
              <div className="flex justify-between text-[10px] font-mono text-muted-foreground mt-0.5">
                <span>{vMin}</span><span>Assay value</span><span>{vMax}</span>
              </div>
            </div>
          )}
          <div className="border border-border rounded-md max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-card"><tr className="border-b border-border text-left">
                {["Sample", "Type", "Value", "Lat", "Lon"].map((h) => <th key={h} className="px-2 py-1.5 overline">{h}</th>)}
              </tr></thead>
              <tbody className="font-mono">
                {data.slice(0, 200).map((s, i) => (
                  <tr key={i} className="border-b border-border/50" data-testid={`sample-row-${i}`}>
                    <td className="px-2 py-1">{s.sample_id}</td>
                    <td className="px-2 py-1">{s.type}</td>
                    <td className="px-2 py-1">
                      {s.value != null && <span className="inline-block w-2 h-2 rounded-full mr-1 align-middle" style={{ background: grade(s.value) }} />}
                      {s.value ?? "-"} {s.unit || ""}
                    </td>
                    <td className="px-2 py-1">{s.lat}</td>
                    <td className="px-2 py-1">{s.lon}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[11px] text-muted-foreground flex items-start gap-1">
            <MapPin size={13} className="mt-0.5 shrink-0" />
            Samples are plotted on the map. In the AI / ML Prospectivity step you can feed these known points directly into the Random Forest model as training positives.
          </div>
        </>
      )}

      <div className="border-t border-border pt-4 space-y-2">
        <div className="flex items-center justify-between">
          <p className="overline">Geochemical / Geophysical Grids</p>
          {aux.length > 0 && <button className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1" onClick={clearAux} data-testid="aux-clear"><Trash size={12} />Clear</button>}
        </div>
        <p className="text-xs text-muted-foreground">Upload gridded data (GeoTIFF, or CSV/XLSX of lon, lat, value) - soil geochem, magnetics, radiometrics, gravity - and blend it into the prospectivity model.</p>
        <div className="flex gap-2">
          <Input value={gName} onChange={(e) => setGName(e.target.value)} placeholder="Layer name (e.g. Soil Au ppb)" className="h-9 text-xs flex-1" data-testid="aux-name" />
          <Select value={gType} onValueChange={setGType}>
            <SelectTrigger className="h-9 w-28 text-xs" data-testid="aux-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="geochem">Geochem</SelectItem>
              <SelectItem value="geophysics">Geophysics</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <label className="border-2 border-dashed border-border rounded-md p-3 flex items-center justify-center gap-2 cursor-pointer hover:border-primary transition-colors text-xs" data-testid="aux-upload">
          {gBusy ? "Uploading…" : <><Stack size={16} /> Upload grid file (.tif / .csv / .xlsx / .geojson)</>}
          <input type="file" className="hidden" accept=".tif,.tiff,.csv,.xlsx,.geojson,.json" onChange={uploadAux} data-testid="aux-file-input" disabled={gBusy} />
        </label>
        <p className="text-[10px] text-muted-foreground">CSV/XLSX expected columns: <span className="font-mono">lon, lat, value</span> (GeoTIFF uses its own georeferencing).</p>

        {aux.length > 0 && (
          <div className="space-y-2">
            {aux.map((l) => (
              <div key={l.name} className="flex items-center gap-2 border border-border rounded px-2 py-1.5" data-testid={`aux-row-${l.name}`}>
                <img src={l.png} alt={l.name} className="w-9 h-9 rounded object-cover border border-border" style={{ imageRendering: "pixelated" }} />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">{l.name}</div>
                  <Badge className="bg-secondary text-[9px]">{l.type}</Badge>
                </div>
                <Input type="number" value={auxW[l.name] ?? 20} onChange={(e) => setAuxW((w) => ({ ...w, [l.name]: e.target.value }))} className="h-7 w-14 text-xs" title="Blend weight %" data-testid={`aux-weight-${l.name}`} />
                <button className="text-primary text-[11px] hover:underline" onClick={() => setOverlay && setOverlay(activeOverlayKey === `aux:${l.name}` ? null : { key: `aux:${l.name}`, png: l.png, bbox, ramp: l.ramp || (l.type === "geophysics" ? "diverging" : "anomaly"), label: l.name })} data-testid={`aux-show-${l.name}`}>
                  {activeOverlayKey === `aux:${l.name}` ? "Hide" : "Map"}
                </button>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <div className="flex-1"><span className="text-[11px] text-muted-foreground">Spectral weight %</span>
                <Input type="number" value={spectralW} onChange={(e) => setSpectralW(e.target.value)} className="h-8 text-xs mt-0.5" data-testid="blend-spectral-weight" /></div>
              <Button size="sm" className="h-9 mt-4" onClick={blend} data-testid="blend-run"><Stack size={14} className="mr-1" />Blend into prospectivity</Button>
            </div>
            <p className="text-[10px] text-muted-foreground">Blending re-generates ranked targets from the combined spectral + geoscience surface.</p>
            <Button size="sm" variant="outline" className="w-full h-8" onClick={savePreset} data-testid="preset-save">Save current mix as a recipe</Button>
          </div>
        )}

        {presets.length > 0 && (
          <div className="space-y-1.5" data-testid="preset-list">
            <p className="overline">Saved blend recipes</p>
            {presets.map((p) => (
              <div key={p.name} className="flex items-center gap-2 border border-border rounded px-2 py-1.5" data-testid={`preset-${p.name}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">{p.name}</div>
                  <div className="text-[10px] text-muted-foreground font-mono truncate">spectral {p.spectral_weight}% · {(p.aux || []).map((a) => `${a.name} ${a.weight}%`).join(" · ") || "no grids"}</div>
                </div>
                <button className="text-primary text-[11px] hover:underline" onClick={() => applyPreset(p)} data-testid={`preset-apply-${p.name}`}>Apply</button>
                <button className="text-muted-foreground hover:text-destructive" onClick={() => delPreset(p.name)} data-testid={`preset-del-${p.name}`}><Trash size={12} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
